import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Paper,
  Box,
  Typography,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Button,
  CircularProgress,
  Chip
} from '@mui/material';
import {
  EmojiEvents as AchievementIcon,
  ArrowForward as ArrowIcon,
  Event as EventIcon,
  SportsEsports as GameIcon,
  Speed as TrackManiaIcon,
  GolfCourse as SkillIcon,
  Group as SocialIcon
} from '@mui/icons-material';
import { achievementsService } from '../../services/achievements';

// Rarity colors
const RARITY_COLORS = {
  common: '#9d9d9d',
  rare: '#0070dd',
  epic: '#a335ee',
  legendary: '#ff8000'
};

// Category icons
const CATEGORY_ICONS = {
  events: EventIcon,
  tournaments: AchievementIcon,
  games: GameIcon,
  trackmania: TrackManiaIcon,
  skill: SkillIcon,
  social: SocialIcon
};

const RecentAchievements = ({ userId, limit = 5 }) => {
  const navigate = useNavigate();
  const [achievements, setAchievements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadRecentAchievements();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const loadRecentAchievements = async () => {
    if (!userId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const result = await achievementsService.getRecentAchievements(userId, limit);
      if (result.success) {
        setAchievements(result.data);
      } else {
        setError(result.error);
      }
    } catch (err) {
      console.error('Error loading recent achievements:', err);
      setError('Failed to load recent achievements');
    } finally {
      setLoading(false);
    }
  };

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString('de-DE', { month: 'short', day: 'numeric' });
  };

  if (loading) {
    return (
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      </Paper>
    );
  }

  if (error) {
    return (
      <Paper sx={{ p: 3 }}>
        <Typography variant="body2" color="error">{error}</Typography>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AchievementIcon color="primary" />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Recent Achievements
          </Typography>
        </Box>
      </Box>

      {achievements.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <AchievementIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
          <Typography variant="body2" color="text.secondary" gutterBottom>
            No achievements unlocked yet
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Keep participating to unlock achievements!
          </Typography>
        </Box>
      ) : (
        <>
          <List sx={{ maxHeight: 400, overflowY: 'auto' }}>
            {achievements.map((achievement) => {
              const CategoryIcon = CATEGORY_ICONS[achievement.category] || SocialIcon;
              const rarityColor = RARITY_COLORS[achievement.rarity] || RARITY_COLORS.common;
              
              return (
                <ListItem
                  key={achievement.achievement_id || achievement.id || achievement._id}
                  sx={{
                    borderLeft: `4px solid ${rarityColor}`,
                    mb: 1,
                    bgcolor: 'action.hover',
                    borderRadius: 1,
                    '&:hover': {
                      bgcolor: 'action.selected'
                    }
                  }}
                >
                  <ListItemAvatar>
                    <Avatar
                      sx={{
                        bgcolor: rarityColor,
                        width: 48,
                        height: 48
                      }}
                    >
                      <CategoryIcon />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                          {achievement.title}
                        </Typography>
                        <Chip
                          label={`${achievement.points} pts`}
                          size="small"
                          sx={{ height: 20, fontSize: '0.7rem' }}
                        />
                      </Box>
                    }
                    secondary={
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          {achievement.description}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {achievement.unlocked_at && formatTimeAgo(achievement.unlocked_at)}
                        </Typography>
                      </Box>
                    }
                    secondaryTypographyProps={{ component: 'div' }}
                  />
                </ListItem>
              );
            })}
          </List>

          <Button
            fullWidth
            variant="outlined"
            endIcon={<ArrowIcon />}
            sx={{ mt: 2 }}
            onClick={() => navigate('/profile')}
          >
            View All Achievements
          </Button>
        </>
      )}
    </Paper>
  );
};

export default RecentAchievements;

